import { useState, useEffect } from "react";
import moment from "moment";
import "moment-timezone";
import axios from "../config/axios";
import { useConfig } from "./useConfig";

export const useStreamTime = () => {
  const { config } = useConfig();

  const [loading, setLoading] = useState(true);
  const [loadingVip, setLoadingVip] = useState(true);
  const [showStream, setShowStream] = useState(null);
  const [showVipStream, setShowVipStream] = useState(null);
  const [offStreamVideo, setOffStreamVideo] = useState("stream-off-vid.mp4");
  const [vipOffStreamVideo, setVipOffStreamVideo] = useState("FWended.mp4");
  const [showStreamLoader, setShowStreamLoader] = useState(false);
  const [showVipStreamLoader, setShowVipStreamLoader] = useState(false);
  const [showTimes, setShowTimes] = useState([]);
  const tz = "America/New_York";

  // Effect to handle stream loader
  useEffect(() => {
    if (showStream) {
      setShowStreamLoader(true);
      setTimeout(() => {
        setShowStreamLoader(false);
      }, 10000);
    }
  }, [showStream]);

  useEffect(() => {
    if (showVipStream) {
      setShowVipStreamLoader(true);
      setTimeout(() => {
        setShowVipStreamLoader(false);
      }, 10000);
    }
  }, [showVipStream]);

  // get show time
  const getShowTime = () => {
    axios
      .get("/shows", { withCredentials: true })
      .then((response) => {
        setLoading(false);

        const sortedShows = response.data.shows.sort((a, b) => {
          if (a._id === "SHOW_1") return -1;
          if (b._id === "SHOW_1") return 1;
          return 0;
        });
        setShowTimes(sortedShows);
      })
      .catch(() => {
        // Handle error silently
      });
  };

  useEffect(() => {
    getShowTime();
  }, []);

  const timeLogic = () => {
    const currentTime = moment().tz("America/New_York"); // EST timezone

    // set show stream to true if current time is between configured times
    if (
      currentTime.format("HH:mm") >= config.terraceOpenTime &&
      currentTime.format("HH:mm") <= config.terraceCloseTime
    ) {
      setShowStream(true);
    } else if (
      currentTime.format("HH:mm") > config.terraceCloseTime &&
      currentTime.format("HH:mm") <= config.fWendedvideo
    ) {
      setShowStream(false);
      setOffStreamVideo("FWended.mp4");
    } else {
      setShowStream(false);
      setOffStreamVideo("stream-off-vid.mp4");
    }

    // if current minute is 0, get show time
    if (currentTime.format("mm") === "00") {
      getShowTime();
    }

    setLoadingVip(false);
  };

  const vipTimeLogic = () => {
    console.log("VIP time logic was trigged", config);
    const currentTime = moment().tz("America/New_York"); // EST timezone

    // normal logic
    let vipShowOneStartTime = config.vipShowOneStartTime
      ? moment
          .tz(config.vipShowOneStartTime, "HH:mm", tz)
          .subtract(5, "minutes")
          .format("HH:mm")
      : moment
          .tz(config.mkFWmanualVIP, "HH:mm", tz)
          .subtract(5, "minutes")
          .format("HH:mm");
    let vipShowOneCloseTime = moment
      .tz(vipShowOneStartTime, "HH:mm", tz)
      .add(35, "minutes")
      .format("HH:mm");

    // check for override
    if (config.override) {
      vipShowOneStartTime = config.vipOverrideOpen;
      vipShowOneCloseTime = config.vipOverrideClose;
    }

    console.log(vipShowOneStartTime, vipShowOneCloseTime);

    // set show stream to true if current time is between configured times
    if (
      currentTime.format("HH:mm") >= vipShowOneStartTime &&
      currentTime.format("HH:mm") <= vipShowOneCloseTime
    ) {
      setVipOffStreamVideo(null);
      setShowVipStream(true);
    } else if (
      currentTime.format("HH:mm") > vipShowOneCloseTime &&
      currentTime.format("HH:mm") <= config.fWendedvideo
    ) {
      console.log("VIP off stream video was trigged");
      setShowVipStream(false);
      setVipOffStreamVideo("FWended.mp4");
    } else {
      setVipOffStreamVideo(null);
      setShowVipStream(false);
    }
    console.log("VIP time logic ended");
  };

  // Time tracker - run every minute
  useEffect(() => {
    const intervalId = setInterval(() => {
      timeLogic();
      vipTimeLogic();
      getShowTime();
      // }, 60000); // 60000ms = 1 minute
    }, 6000); // 6000ms

    // Run immediately on mount
    timeLogic();
    vipTimeLogic();

    return () => clearInterval(intervalId);
  }, [config, showTimes]);

  // Show time helpers
  const getShowTimeText = () => {
    if (showTimes.length > 0) {
      if (showTimes[0].startTime === null && showTimes[1].startTime === null) {
        return "No Shows Tonight";
      }

      if (showTimes[0].startTime === null && showTimes[1].startTime !== null) {
        return `EPCOT ${moment(showTimes[1].startTime)
          .tz(tz)
          .format("LT")} (EST) / Magic Kingdom ${config.mkFWManual}`;
      }

      if (showTimes[0].startTime !== null && showTimes[1].startTime === null) {
        return `Magic Kingdom ${moment(showTimes[0].startTime)
          .tz(tz)
          .format("LT")} (EST)`;
      }

      if (showTimes[0].startTime !== null && showTimes[1].startTime !== null) {
        return `EPCOT ${moment(showTimes[1].startTime)
          .tz(tz)
          .format("LT")} (EST) / Magic Kingdom ${moment(showTimes[0].startTime)
          .tz(tz)
          .format("LT")} (EST)`;
      }
    }
  };

  const getEpcotShowTimeText = () => {
    if (showTimes.length > 0) {
      if (showTimes[1].startTime === null) {
        return "No Show";
      }
      return moment.tz(showTimes[1].startTime, tz).format("LT") + " (EST)";
    }
  };

  const getMagicKingdomShowTimeText = () => {
    if (showTimes.length > 0) {
      if (showTimes[0].startTime === null) {
        return config.mkFWManual;
      }
      return moment.tz(showTimes[0].startTime, tz).format("LT") + " (EST)";
    }
  };

  const getMagicKingVipShowTime = () => {
    // normal logic
    let vipShowOneStartTime = config.vipShowOneStartTime
      ? moment.tz(config.vipShowOneStartTime, "HH:mm", tz).format("LT")
      : moment.tz(config.mkFWmanualVIP, "HH:mm", tz).format("LT");

    // We don't consider the override when showing the VIP show time
    // if (config.override) {
    //   vipShowOneStartTime = config.vipOverrideOpen;
    // }

    return vipShowOneStartTime + " (EST)";
  };

  return {
    loading,
    showStream,
    offStreamVideo,
    showStreamLoader,
    showTimes,
    tz,
    showVipStream,
    getShowTimeText,
    getEpcotShowTimeText,
    getMagicKingdomShowTimeText,
    showVipStreamLoader,
    getMagicKingVipShowTime,
    vipOffStreamVideo,
    loadingVip,
  };
};
